import {
    SpacingToken,
    UiStack,
    UiText,
} from '@experiences/ui-common';
import { useRegionDisplay } from '@experiences/util';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SettingsIcon from '@mui/icons-material/Settings';
import Link from '@mui/material/Link';
import type { Theme } from '@mui/material/styles';
import { FontVariantToken } from '@uipath/apollo-core';
import {
    ApButton,
    ApStepper,
    ApStepperStep,
    PortalAlertBar,
} from '@uipath/portal-shell-react';
import React from 'react';
import { useIntl } from 'react-intl';

import { DataResidencyCloudLink } from '../../../common/constants/documentation/DocumentationLinksDefault';
import { DataResidencyCloudLinkK8s } from '../../../common/constants/documentation/DocumentationLinksK8s';
import { getFriendlyName } from '../../../common/constants/ServicesMapping';
import { useDocumentationLinks } from '../../../common/hooks/useDocumentationLink';
import useIsOnPrem from '../../../common/hooks/useIsOnPrem';
import type { IServiceMigrationStatus } from '../../../common/interfaces/gws';
import { useTelemetryHelper } from '../../../telemetry/TelemetryHelper';

/** Shared status-chip colour classes used by progress grid & list */
export const statusChipStyles = (theme: Theme) => ({
    migratedChip: {
        backgroundColor: theme.palette.semantic.colorSuccessBackground,
        color: theme.palette.semantic.colorSuccessText,
    },
    runningChip: {
        backgroundColor: theme.palette.semantic.colorInfoBackground,
        color: theme.palette.semantic.colorInfoText,
    },
    failedChip: {
        backgroundColor: theme.palette.semantic.colorErrorBackground,
        color: theme.palette.semantic.colorErrorText,
    },
    pendingChip: {
        backgroundColor: theme.palette.semantic.colorBackgroundDisabled,
        color: theme.palette.semantic.colorForegroundDeEmp,
    },
});

export const getChipClassName = (status: string, classes: Record<string, string>): string => {
    switch (status) {
        case 'Migrated':
            return classes.migratedChip;
        case 'Running':
            return classes.runningChip;
        case 'Failed':
            return classes.failedChip;
        default:
            return classes.pendingChip;
    }
};

/** Display-friendly service name using the ServicesMapping lookup */
export const getServiceDisplayName = (serviceName: string): string => getFriendlyName(serviceName);

/**
 * Extract a human-readable reason from the raw error JSON returned by the progress API.
 * The API returns a JSON array like: [{"Message":"...","ServiceNames":[],...}]
 * This extracts just the Message fields joined together.
 */
export const parseErrorReason = (errorMessage: string | null | undefined): string | null => {
    if (!errorMessage) {
        return null;
    }
    try {
        const parsed = JSON.parse(errorMessage);
        if (Array.isArray(parsed)) {
            const messages = parsed
                .map((entry: { Message?: string }) => entry.Message)
                .filter(Boolean);
            return messages.length > 0 ? messages.join('; ') : errorMessage;
        }
    } catch {
        // Not JSON — return as-is
    }
    return errorMessage;
};

/**
 * Check whether any service in the list is still in a non-terminal state.
 * Used to override "PartiallyCompleted" → "In progress" when services are still pending/running.
 */
export const hasNonTerminalServices = (services: IServiceMigrationStatus[]): boolean =>
    services.some(s => s.status === 'Pending' || s.status === 'Running');

/** Shared service card grid + card styles used by ProgressGrid & CategoryGrid */
export const serviceCardStyles = (theme: Theme) => ({
    grid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 330px))',
        gap: '16px',
    },
    serviceCard: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        border: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
        padding: '16px',
        borderRadius: '3px',
    },
    cardTitle: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
    },
});

/** Shared page-level layout styles (header + footer) used by multiple migration pages */
export const pageLayoutStyles = (theme: Theme) => ({
    header: {
        marginTop: '40px',
        padding: '0 24px',
    },
    contentWrapper: {
        padding: '16px 24px 0',
        maxWidth: '1152px',
    },
    footer: {
        position: 'fixed' as const,
        bottom: '0px',
        right: '0px',
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        borderTop: `1px solid ${theme.palette.semantic.colorBorderDeEmp}`,
        height: '64px',
        backgroundColor: theme.palette.semantic.colorBackground,
        zIndex: 1,
        width: '100%',
    },
    button: { minWidth: '120px' },
});

/** Route display column showing source → destination region */
export const MigrationRouteDisplay: React.FC<{
    sourceRegion: string;
    destinationRegion: string;
}> = ({
    sourceRegion, destinationRegion,
}) => {
    const { formatMessage: translate } = useIntl();
    const getRegionDisplayValue = useRegionDisplay();
    return (
        <UiStack
            direction="column"
            gap={SpacingToken.Micro}>
            <UiText
                color="var(--color-foreground-de-emp)"
                variant={FontVariantToken.fontSizeSBold}>
                {translate({ id: 'CLIENT_MIGRATION_PROGRESS_ROUTE' })}
            </UiText>
            <UiText variant={FontVariantToken.fontSizeM}>
                {getRegionDisplayValue(sourceRegion)}
                {' '}
→
                {getRegionDisplayValue(destinationRegion)}
            </UiText>
        </UiStack>
    );
};

/** Shared wizard-page layout styles used by RetryWizard & EditSchedulePage */
export const wizardLayoutStyles = (theme: Theme) => ({
    ...pageLayoutStyles(theme),
    mainDisplay: {
        display: 'flex',
        width: '100%',
    },
    standardLayout: { flex: 3 },
    stepper: { flex: 1 },
    content: {
        display: 'flex',
        alignItems: 'flex-start',
        flexDirection: 'column' as const,
        width: '100%',
        maxWidth: '800px',
        marginBottom: '64px',
    },
});

/** Timezone info alert bar shown in scheduling steps */
export const MigrationTimezoneAlert: React.FC<{ tz: { name: string; short: string; utcOffset: string } }> = ({ tz }) => {
    const { formatMessage: translate } = useIntl();
    return (
        <PortalAlertBar
            status="info"
            cancelable={false}>
            <UiText variant={FontVariantToken.fontSizeS}>
                {translate({ id: 'CLIENT_MIGRATION_TIMEZONE_INFO' }, {
                    timezone: tz.name,
                    short: tz.short,
                    utcOffset: tz.utcOffset,
                })}
            </UiText>
        </PortalAlertBar>
    );
};

/** "What happens next" info box shown in scheduling steps */
export const MigrationWhatsNextBox: React.FC<{
    whatsNextKey: string;
    timeInfoKeys: readonly string[];
    className?: string;
}> = ({
    whatsNextKey, timeInfoKeys, className,
}) => {
    const { formatMessage: translate } = useIntl();
    return (
        <div className={className}>
            <UiText variant={FontVariantToken.fontSizeMBold}>
                {translate({ id: whatsNextKey })}
            </UiText>
            <ul>
                {timeInfoKeys.map((key) => (
                    <li key={key}>
                        <UiText>
                            {translate({ id: key })}
                        </UiText>
                    </li>
                ))}
            </ul>
        </div>
    );
};

/** Shared wizard footer with Cancel / Previous / Next / Submit buttons */
export const MigrationWizardFooter: React.FC<{
    activeStep: number;
    onCancel: () => void;
    onPrevious: () => void;
    onNext: () => void;
    nextDisabled?: boolean;
    submitLabel: string;
    submitLoading?: boolean;
    onSubmit: () => void;
    footerClassName: string;
    buttonClassName?: string;
}> = ({
    activeStep,
    onCancel,
    onPrevious,
    onNext,
    nextDisabled,
    submitLabel,
    submitLoading,
    onSubmit,
    footerClassName,
    buttonClassName,
}) => {
    const { formatMessage: translate } = useIntl();
    return (
        <UiStack
            gap={SpacingToken.XS}
            pr={SpacingToken.M}
            className={footerClassName}>
            <ApButton
                variant="tertiary"
                label={translate({ id: 'CLIENT_CANCEL' })}
                onClick={onCancel}
            />
            {activeStep > 0 && (
                <ApButton
                    variant="secondary"
                    className={buttonClassName}
                    label={translate({ id: 'CLIENT_PREVIOUS' })}
                    onClick={onPrevious}
                />
            )}
            {activeStep === 0 && (
                <ApButton
                    variant="primary"
                    className={buttonClassName}
                    label={translate({ id: 'CLIENT_NEXT' })}
                    disabled={nextDisabled}
                    onClick={onNext}
                />
            )}
            {activeStep === 1 && (
                <ApButton
                    variant="primary"
                    className={buttonClassName}
                    label={translate({ id: submitLabel })}
                    loading={submitLoading}
                    onClick={onSubmit}
                />
            )}
        </UiStack>
    );
};

/** Service card with icon, name, completedAt date, and chip */
export const MigrationServiceCard: React.FC<{
    service: IServiceMigrationStatus;
    icon: React.ReactNode;
    chip: React.ReactNode;
    cardClassName: string;
    titleClassName: string;
}> = ({
    service, icon, chip, cardClassName, titleClassName,
}) => {
    const {
        formatMessage: translate, formatDate,
    } = useIntl();
    return (
        <div className={cardClassName}>
            <div className={titleClassName}>
                {icon}
                <UiStack
                    direction="column"
                    gap={SpacingToken.Micro}>
                    <UiText variant={FontVariantToken.fontSizeMBold}>
                        {getServiceDisplayName(service.serviceName)}
                    </UiText>
                    {service.completedAt && (
                        <UiText
                            variant={FontVariantToken.fontSizeXs}
                            color="var(--color-foreground-de-emp)">
                            {translate(
                                { id: 'CLIENT_MIGRATION_SERVICE_MIGRATED_ON' },
                                {
                                    date: formatDate(new Date(service.completedAt), {
                                        month: 'short',
                                        day: 'numeric',
                                    }),
                                },
                            )}
                        </UiText>
                    )}
                </UiStack>
            </div>
            {chip}
        </div>
    );
};

/** Stepper sidebar for wizard pages */
export const MigrationStepperSidebar: React.FC<{
    activeStep: number;
    steps: string[];
    className: string;
}> = ({
    activeStep, steps, className,
}) => {
    const { formatMessage: translate } = useIntl();
    return (
        <div className={className}>
            <ApStepper
                activeStep={activeStep}
                vertical>
                {steps.map((label) => (
                    <ApStepperStep
                        key={label}
                        label={translate({ id: label })} />
                ))}
            </ApStepper>
        </div>
    );
};

/** Shared styles for the region selection step pages */
export const regionSelectStyles = () => ({
    regionSource: { width: '320px' },
    regionTarget: { width: '320px' },
    infoLink: {
        fontSize: '14px',
        fontWeight: 600,
        display: 'flex',
        flexDirection: 'row' as const,
        alignItems: 'center',
    },
    arrowIcon: { marginTop: '42px' },
    infoLinkIcon: {
        height: '16px',
        width: '16px',
        marginLeft: '4px',
        justifyItems: 'center',
    },
});

/** Platform Services info card shown in the select-region step */
export const PlatformServicesInfoCard: React.FC = () => {
    const { formatMessage: translate } = useIntl();
    return (
        <UiStack
            align="center"
            gap={SpacingToken.S}
            style={{
                border: '1px solid var(--color-border-de-emp)',
                borderRadius: '3px',
                padding: '16px',
            }}>
            <SettingsIcon sx={{
                fontSize: 20,
                color: 'action.active',
            }} />
            <UiStack
                direction="column"
                gap={SpacingToken.Micro}
                style={{ flex: 1 }}>
                <UiText variant={FontVariantToken.fontSizeMBold}>
                    {translate({ id: 'CLIENT_MIGRATION_PLATFORM_SERVICES' })}
                </UiText>
                <UiText
                    variant={FontVariantToken.fontSizeS}
                    color="var(--color-foreground-de-emp)">
                    {translate({ id: 'CLIENT_MIGRATION_PLATFORM_SERVICES_DESCRIPTION' })}
                </UiText>
            </UiStack>
        </UiStack>
    );
};

/** "Services available in region" header with documentation link */
export const MigrationServiceAvailabilityHeader: React.FC<{
    descriptionKey: string;
    telemetryEvent: string;
    telemetryLocation: string;
}> = ({
    descriptionKey, telemetryEvent, telemetryLocation,
}) => {
    const { formatMessage: translate } = useIntl();
    const getLocalizedLinks = useDocumentationLinks();
    const isOnPrem = useIsOnPrem();
    const { logEvent } = useTelemetryHelper();
    return (
        <UiStack
            direction="column"
            mt={SpacingToken.L}
            gap={SpacingToken.Micro}>
            <UiText variant={FontVariantToken.fontSizeLBold}>
                {translate({ id: 'CLIENT_SERVICE_AVAILABLE' })}
            </UiText>
            <UiText variant={FontVariantToken.fontSizeM}>
                {translate({ id: descriptionKey })}
            </UiText>
            <UiStack>
                <Link
                    sx={{
                        fontSize: '14px',
                        fontWeight: 600,
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'center',
                    }}
                    onClick={() => logEvent(telemetryEvent, { Location: telemetryLocation })}
                    target="_blank"
                    href={getLocalizedLinks({ articleSlug: isOnPrem ? DataResidencyCloudLinkK8s : DataResidencyCloudLink })}>
                    {translate({ id: 'CLIENT_READ_REGION_RESIDENCY' })}
                    <OpenInNewIcon sx={{
                        height: '16px',
                        width: '16px',
                        marginLeft: '4px',
                    }} />
                </Link>
            </UiStack>
        </UiStack>
    );
};
